import {
  offerApi,
  AcceptedInquiryAlignment,
  OfferGroupDetail,
  OfferedOffer,
} from '@texas/api/endpoints/offerApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { ServerError } from '@texas/types';
import { useDisclosure } from '@chakra-ui/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface RemoveCurrencySelection {
  branchId: number;
  currencyId: number;
}

interface OfferContextValue {
  offerGroupId: number;
  variantId: number;
  group: OfferGroupDetail | null;
  loading: boolean;
  error: ServerError | null;
  findCurrency: (id: number) => { code: string } | undefined;
  refreshGroup: () => void;
  offeredOffers: OfferedOffer[] | null;
  offeredOffersLoading: boolean;
  groupOfferedOffers: OfferedOffer[];
  refreshOfferedOffers: () => void;
  settingMatch: boolean;
  removingCurrency: boolean;
  editingMatchedInquiry: AcceptedInquiryAlignment | null;
  editingOfferId: number | null;
  isEditingOfferOpen: boolean;
  closeEditingOffer: () => void;
  copyingOfferId: number | null;
  isCopyingOfferOpen: boolean;
  openCopyingOffer: (offerId: number) => void;
  closeCopyingOffer: () => void;
  selectedOfferedOfferId: number | null;
  isOfferedOfferOpen: boolean;
  openOfferedOffer: (offeredOfferId: number) => void;
  closeOfferedOffer: () => void;
  removeCurrencySelection: RemoveCurrencySelection | null;
  isRemoveCurrencyOpen: boolean;
  closeRemoveCurrency: () => void;
  removeSelectedCurrency: () => Promise<void>;
  isAddBranchOpen: boolean;
  openAddBranch: () => void;
  closeAddBranch: () => void;
  isDefaultEditingCurrency: boolean;
  isMatchedInquiryPriceVisible: (inquiryId: number) => boolean;
  toggleMatchedInquiryPrice: (inquiryId: number) => void;
  matchInquiry: (inquiryId: number) => void;
  unmatchInquiry: (inquiryId: number) => void;
  requestRemoveCurrency: (branchId: number, currencyId: number) => void;
  openEditMasterOffer: (
    offerId: number,
    matchedInquiry: AcceptedInquiryAlignment | null,
  ) => void;
}

const OfferContext = createContext<OfferContextValue | null>(null);

interface OfferProviderProps {
  offerGroupId: number;
  variantId: number;
  children: ReactNode;
}

export function OfferProvider({
  offerGroupId,
  variantId,
  children,
}: OfferProviderProps) {
  const {
    data: group,
    loading,
    error,
    refetch: refetchGroup,
  } = useApiResource(offerApi.getGroup);
  const {
    data: offeredOffers,
    loading: offeredOffersLoading,
    refetch: refetchOfferedOffers,
  } = useApiResource(offerApi.getOfferedOffersByVariant);
  const {
    useCurrenciesData: { findCurrency },
    currenciesData: [, refetchCurrencies],
  } = useMetadata();
  const [editingMatchedInquiry, setEditingMatchedInquiry] =
    useState<AcceptedInquiryAlignment | null>(null);
  const [hiddenMatchedInquiryPriceIds, setHiddenMatchedInquiryPriceIds] =
    useState<Set<number>>(new Set());
  const {
    value: editingOfferId,
    isOpen: isEditingOfferOpen,
    onOpen: openEditingOffer,
    onClose: closeEditingOffer,
  } = useValueDisclosure<number>();
  const {
    value: copyingOfferId,
    isOpen: isCopyingOfferOpen,
    onOpen: openCopyingOffer,
    onClose: closeCopyingOffer,
  } = useValueDisclosure<number>();
  const {
    value: selectedOfferedOfferId,
    isOpen: isOfferedOfferOpen,
    onOpen: openOfferedOffer,
    onClose: closeOfferedOffer,
  } = useValueDisclosure<number>();
  const {
    value: removeCurrencySelection,
    isOpen: isRemoveCurrencyOpen,
    onOpen: openRemoveCurrency,
    onClose: closeRemoveCurrency,
  } = useValueDisclosure<RemoveCurrencySelection>();
  const {
    isOpen: isAddBranchOpen,
    onOpen: openAddBranch,
    onClose: closeAddBranch,
  } = useDisclosure();

  const { request: setMatchingInquiry, loading: settingMatch } = useApiRequest(
    offerApi.setMatchingInquiry,
  );
  const { request: removeCurrency, loading: removingCurrency } = useApiRequest(
    offerApi.removeCurrency,
  );

  useEffect(() => {
    refetchCurrencies();
  }, [refetchCurrencies]);

  useEffect(() => {
    refetchGroup(offerGroupId);
    refetchOfferedOffers(variantId);
  }, [refetchGroup, refetchOfferedOffers, offerGroupId, variantId]);

  const refreshGroup = useCallback(() => {
    refetchGroup(offerGroupId);
  }, [refetchGroup, offerGroupId]);

  const refreshOfferedOffers = useCallback(() => {
    refetchOfferedOffers(variantId);
  }, [refetchOfferedOffers, variantId]);

  const groupOfferedOffers = useMemo(
    () =>
      (offeredOffers ?? []).filter(
        (offeredOffer) => offeredOffer.offerGroupId === offerGroupId,
      ),
    [offeredOffers, offerGroupId],
  );

  const editingBranch = editingOfferId
    ? group?.branches.find((branch) =>
        branch.currencies.some(
          (currency) => currency.masterOffer?.id === editingOfferId,
        ),
      )
    : null;

  const editingCurrency = editingBranch
    ? editingBranch.currencies.find(
        (currency) => currency.masterOffer?.id === editingOfferId,
      )
    : null;

  const isDefaultEditingCurrency =
    !!editingBranch &&
    !!editingCurrency &&
    editingCurrency.currencyId === editingBranch.branchDefaultCurrencyId;

  const matchedInquiryIds = useMemo(
    () =>
      group?.branches
        .flatMap((branch) => branch.acceptedInquiries)
        .filter((inquiry) => inquiry.isMatched)
        .map((inquiry) => inquiry.inquiryId) ?? [],
    [group],
  );

  const handleSetMatchingInquiry = useCallback(
    async (inquiryIds: number[]) => {
      const res = await setMatchingInquiry(offerGroupId, inquiryIds);
      if (!res.hasError) {
        refreshGroup();
      }
    },
    [offerGroupId, refreshGroup, setMatchingInquiry],
  );

  const matchInquiry = useCallback(
    (inquiryId: number) => {
      handleSetMatchingInquiry([...new Set([...matchedInquiryIds, inquiryId])]);
    },
    [handleSetMatchingInquiry, matchedInquiryIds],
  );

  const unmatchInquiry = useCallback(
    (inquiryId: number) => {
      handleSetMatchingInquiry(
        matchedInquiryIds.filter(
          (matchedInquiryId) => matchedInquiryId !== inquiryId,
        ),
      );
    },
    [handleSetMatchingInquiry, matchedInquiryIds],
  );

  const requestRemoveCurrency = useCallback(
    (branchId: number, currencyId: number) => {
      openRemoveCurrency({ branchId, currencyId });
    },
    [openRemoveCurrency],
  );

  const isMatchedInquiryPriceVisible = useCallback(
    (inquiryId: number) => !hiddenMatchedInquiryPriceIds.has(inquiryId),
    [hiddenMatchedInquiryPriceIds],
  );

  const toggleMatchedInquiryPrice = useCallback((inquiryId: number) => {
    setHiddenMatchedInquiryPriceIds((current) => {
      const next = new Set(current);
      if (next.has(inquiryId)) {
        next.delete(inquiryId);
      } else {
        next.add(inquiryId);
      }

      return next;
    });
  }, []);

  const removeSelectedCurrency = useCallback(async () => {
    if (!removeCurrencySelection) return;
    const res = await removeCurrency(
      offerGroupId,
      removeCurrencySelection.branchId,
      removeCurrencySelection.currencyId,
    );
    if (!res.hasError) {
      refreshGroup();
    }
  }, [offerGroupId, refreshGroup, removeCurrency, removeCurrencySelection]);

  const openEditMasterOffer = useCallback(
    (offerId: number, matchedInquiry: AcceptedInquiryAlignment | null) => {
      setEditingMatchedInquiry(matchedInquiry);
      openEditingOffer(offerId);
    },
    [openEditingOffer],
  );

  const value = useMemo<OfferContextValue>(
    () => ({
      offerGroupId,
      variantId,
      group,
      loading,
      error,
      findCurrency,
      refreshGroup,
      offeredOffers,
      offeredOffersLoading,
      groupOfferedOffers,
      refreshOfferedOffers,
      settingMatch,
      removingCurrency,
      editingMatchedInquiry,
      editingOfferId,
      isEditingOfferOpen,
      closeEditingOffer,
      copyingOfferId,
      isCopyingOfferOpen,
      openCopyingOffer,
      closeCopyingOffer,
      selectedOfferedOfferId,
      isOfferedOfferOpen,
      openOfferedOffer,
      closeOfferedOffer,
      removeCurrencySelection,
      isRemoveCurrencyOpen,
      closeRemoveCurrency,
      removeSelectedCurrency,
      isAddBranchOpen,
      openAddBranch,
      closeAddBranch,
      isDefaultEditingCurrency,
      isMatchedInquiryPriceVisible,
      toggleMatchedInquiryPrice,
      matchInquiry,
      unmatchInquiry,
      requestRemoveCurrency,
      openEditMasterOffer,
    }),
    [
      closeAddBranch,
      closeCopyingOffer,
      closeEditingOffer,
      closeOfferedOffer,
      closeRemoveCurrency,
      copyingOfferId,
      editingMatchedInquiry,
      editingOfferId,
      error,
      findCurrency,
      group,
      groupOfferedOffers,
      isAddBranchOpen,
      isCopyingOfferOpen,
      isDefaultEditingCurrency,
      isMatchedInquiryPriceVisible,
      isEditingOfferOpen,
      isOfferedOfferOpen,
      isRemoveCurrencyOpen,
      loading,
      matchInquiry,
      offeredOffers,
      offeredOffersLoading,
      offerGroupId,
      openAddBranch,
      openCopyingOffer,
      openEditMasterOffer,
      openOfferedOffer,
      refreshOfferedOffers,
      refreshGroup,
      removeCurrencySelection,
      removeSelectedCurrency,
      removingCurrency,
      requestRemoveCurrency,
      selectedOfferedOfferId,
      settingMatch,
      toggleMatchedInquiryPrice,
      unmatchInquiry,
      variantId,
    ],
  );

  return (
    <OfferContext.Provider value={value}>{children}</OfferContext.Provider>
  );
}

export function useOffer() {
  const context = useContext(OfferContext);
  if (!context) {
    throw new Error('useOffer must be used within OfferProvider');
  }
  return context;
}
